import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Link from 'components/Link';
import buildAffiliateLink from 'lib/buildAffiliateLink';
import { stub as $t } from '@nbcnews/analytics-framework';

import { getAmazonUrlProductAsinAndAssociateTag } from 'lib/commerceUtils';
import { isLengthGreaterThan8, getFontSizeFromCharLength } from 'lib/fontBasedOnTextLength';
import { getDateTime } from 'lib/DateTime';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { isOnSale, getPercentOff } from 'lib/productOfferPriceHelpers';

import { UNIVERSAL_CHECKOUT_ENABLED } from 'lib/brandFeatures';
import ShopButton from './ShopButton';

import './styles.themed.scss';

const ENCODER_STOP_DATE = 'Wed Apr 15 2020 00:00:00';

/**
 * Manages the button type to render.
 * @param {object} props
 * @param {string} props.productId
 * @param {string} props.name
 * @param {string} props.listPrice
 * @param {string} props.salePrice
 * @param {string} props.productUrl
 * @param {string} props.priceDiscountPrefixText
 * @param {bool} props.isProductUnit
 * @param {bool} props.showPercentOff
 * @param {string} props.seller
 * @param {object} props.additionalStyles
 * @param {string} props.block
 * @param {boolean} props.isLink
 * @param {string} props.articlePublishDate
 * @param {string} props.vertical
 * @param {string} props.target
 * @param {string} props.trackingCode
 * @param {object} context
 * @param {string} context.canonicalUrl
 */
const ShopButtonInfo = (
  {
    productId,
    name,
    listPrice,
    salePrice = '',
    productUrl,
    priceDiscountPrefixText = '',
    isProductUnit = false,
    showPercentOff = false,
    seller,
    additionalStyles = {},
    block = 'ListicleProduct',
    isLink = true,
    articlePublishDate = '',
    vertical = '',
    target = '_blank',
    trackingCode = 'mbt_ecommerce_shop_button',
  }, { canonicalUrl = '' },
) => {
  const universalCheckoutEnabled = getFeatureConfigForBrand(
    UNIVERSAL_CHECKOUT_ENABLED,
    vertical,
  );
  const { t } = useTranslation();

  useEffect(() => {
    $t('register', trackingCode, { allowDuplicate: true });
  }, []);

  /**
    If an articlePublishDate is provided we need to determine if the articlePublishDate
    is after the ENCODER_STOP_DATE (isCurrentArticlePublishedAfterEncoderStopDate)
    because then we wouldn't need to build the affiliate link in Ramen
  */
  let isCurrentArticlePublishedAfterEncoderStopDate;
  let affiliateUrl;
  if (articlePublishDate) {
    const fetchedEncoderStopDate = getDateTime(ENCODER_STOP_DATE);
    const fetchedCurrentArticlePublishDate = getDateTime(articlePublishDate);
    // eslint-disable-next-line max-len
    isCurrentArticlePublishedAfterEncoderStopDate = fetchedCurrentArticlePublishDate > fetchedEncoderStopDate;
    affiliateUrl = isCurrentArticlePublishedAfterEncoderStopDate
      ? productUrl
      : buildAffiliateLink({ url: productUrl, canonicalUrl });
  } else {
    affiliateUrl = productUrl;
  }
  const isWaffle = block === 'WaffleProduct';

  const isLongerThan8Chars = isLengthGreaterThan8(seller);
  const bemElemPriceBtn = `${block}__priceBtn`;
  const costStyles = [bemElemPriceBtn, additionalStyles.cost,
    { 'shop-button-info__cost-fit-text': isLongerThan8Chars && !showPercentOff }];

  const shouldShowStrikeThroughPricing = isOnSale({ salePrice, listPrice });
  const percentOffPrefixText = priceDiscountPrefixText || t('Now');
  const sellerFontSize = isProductUnit ? 'f4' : getFontSizeFromCharLength(seller);
  const percentOff = getPercentOff(listPrice, salePrice);
  const shouldShowPercentOff = !!percentOff && showPercentOff && shouldShowStrikeThroughPricing;

  /**
   * Helper function to render the price and seller name.
   */
  const renderPrice = () => (
    <>
      {shouldShowStrikeThroughPricing && (
        <div className="shop-button-info-price shop-button-info__sale-cost">
          $
          {salePrice}
        </div>
      )}
      <div className={classNames('shop-button-info-price shop-button-info__list-cost', { 'strike fw5': shouldShowStrikeThroughPricing })}>
        $
        {listPrice}
      </div>
      {isProductUnit && (
        <div className={classNames('shop-button-info-price shop-button-info__vendor-name-v2', sellerFontSize)}>
          {seller}
        </div>
      )}
    </>
  );

  const body = isProductUnit ? (
    <div className="shop-button-info__wrapper-outer" data-testid="shop-button-info__product-unit">
      <div className="shop-button-info__wrapper-inner">
        <span className={classNames(costStyles)}>
          {renderPrice()}
        </span>
      </div>
      {shouldShowPercentOff && (
        <div className="shop-button-info__percent-off" data-testid="percent-off">
          {`${percentOffPrefixText} ${percentOff}% off`}
        </div>
      )}
    </div>
  ) : (
    <>
      <ShopButton
        additionalStyles={additionalStyles}
        block={block}
        isWaffle={isWaffle}
        listPrice={listPrice}
        salePrice={salePrice}
        name={name}
        seller={seller}
        shouldTrackClicks={false} // click already tracked here in parent
        trackingCode={trackingCode}
      />
    </>
  );

  if (isLink) {
    const href = universalCheckoutEnabled ? productUrl : affiliateUrl;

    const { productAsin, associateTag } = getAmazonUrlProductAsinAndAssociateTag(href);

    return (
      <Link
        className={classNames(
          `${block}__shopButtons`,
          {
            'shop-button-info__shop-fit-text': isLongerThan8Chars && !isProductUnit,
          },
          additionalStyles.shop,
        )}
        data-aps-asc-tag={associateTag}
        data-aps-asin={productAsin}
        data-product-id={productId}
        data-testid="shop-button-info-link"
        href={href}
        key={affiliateUrl}
        onClick={() => {
          $t('track', trackingCode, {
            action: 'itemClick',
            name,
            listPrice,
            seller,
          });
        }}
        rel="sponsored noopener noreferrer"
        target={isWaffle ? target : '_blank'}
      >
        {body}
      </Link>

    );
  }

  return (
    <div
      className={classNames(
        `${block}__shopButtons`,
        { 'shop-button-info__shop-fit-text': isLongerThan8Chars && !isProductUnit },
        additionalStyles.shop,
      )}
      key={affiliateUrl}
      data-testid="shop-button-info"
    >
      {body}
    </div>
  );
};

export default ShopButtonInfo;
